import { useState } from "react";
import { t, formatDate } from "../../shared/trendReportShared";

const STATUS_STYLES = {
  sent: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400",
  partial:
    "bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-400",
  failed: "bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-400",
  running: "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300",
};

const STATUS_LABELS = {
  sent: "newsletterStatusSent",
  partial: "newsletterStatusPartial",
  failed: "newsletterStatusFailed",
  running: "newsletterStatusRunning",
};

function StatusPill({ status, language }) {
  return (
    <span
      className={`shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
        STATUS_STYLES[status] || STATUS_STYLES.running
      }`}
    >
      {t(STATUS_LABELS[status] || "newsletterStatusRunning", language)}
    </span>
  );
}

// Counts here are emails, not people: a recipient targeted in both languages
// receives two (see the dispatch_report docstring), so the figure can exceed
// the number of names on the recipient list.
function Detail({ d, language }) {
  return (
    <>
      {d.sent_count}/{d.recipient_count} {t("newsletterEmailsCount", language)}
      {d.failed_count > 0 && ` · ${d.failed_count} ✕`}
      {d.trimmed_items > 0 &&
        ` · ${d.trimmed_items} ${t("newsletterTrimmed", language)}`}
    </>
  );
}

// Only the most recent send is worth reading at a glance - it answers "did
// today's brief go out?". Everything older is audit trail, so it stays one
// click away rather than pushing the panel down the page.
export default function DispatchHistory({ language, dispatches }) {
  const [showEarlier, setShowEarlier] = useState(false);

  const items = dispatches.items || [];
  const [latest, ...earlier] = items;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
      <h3 className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-3">
        {t("newsletterHistory", language)}
      </h3>
      {dispatches.status === "loading" && (
        <p className="text-xs text-gray-400">
          {t("newsletterLoading", language)}
        </p>
      )}
      {dispatches.status === "error" && (
        <p className="text-xs text-red-500">{t("newsletterError", language)}</p>
      )}
      {dispatches.status === "ok" && items.length === 0 && (
        <p className="text-xs text-gray-400">
          {t("newsletterNoHistory", language)}
        </p>
      )}

      {latest && (
        <div className="rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-700/60 px-4 py-3.5">
          <div className="flex items-center justify-between gap-3">
            <span className="text-[10px] font-bold uppercase tracking-wide text-orange-600 dark:text-orange-400">
              {t("newsletterLatestActivity", language)}
            </span>
            <StatusPill status={latest.status} language={language} />
          </div>
          <p className="mt-1.5 text-sm font-semibold text-gray-800 dark:text-gray-100">
            {formatDate(latest.report_date, language)}
            <span className="ml-2 text-[11px] font-normal text-gray-400">
              {latest.trigger}
            </span>
          </p>
          <p className="text-[11px] text-gray-400">
            <Detail d={latest} language={language} />
          </p>
        </div>
      )}

      {earlier.length > 0 && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => setShowEarlier((o) => !o)}
            className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg
              className={`w-3 h-3 transition-transform ${showEarlier ? "rotate-90" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
            {t(
              showEarlier ? "newsletterHideEarlier" : "newsletterShowEarlier",
              language,
            )}{" "}
            ({earlier.length})
          </button>

          {showEarlier && (
            <ul className="mt-3 space-y-2">
              {earlier.map((d) => (
                <li
                  key={d.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-gray-100 dark:border-gray-700/60 px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-gray-700 dark:text-gray-200">
                      {formatDate(d.report_date, language)}
                      <span className="ml-2 text-[11px] font-normal text-gray-400">
                        {d.trigger}
                      </span>
                    </p>
                    <p className="text-[11px] text-gray-400">
                      <Detail d={d} language={language} />
                    </p>
                  </div>
                  <StatusPill status={d.status} language={language} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
